<template>
  <v-container>
    <v-row class="mb-1">
      <v-col>
        <div class="text-h5 font-weight-bold">
          ລາຍງານເກັບເງິນ AR ພາກສະໜາມ
        </div>
        <div class="text-body-2 grey--text">
          ເງິນສົດ ແລະ LDB merchant ທີ່ AR ພາກສະໜາມ (role ar_field) ເກັບໄດ້, ຕາມວັນທີຈ່າຍບິນ
        </div>
      </v-col>
    </v-row>

    <v-card
      outlined
      class="mb-4"
    >
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            sm="auto"
          >
            <v-menu
              v-model="dateFromMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFrom"
                  dense
                  hide-details
                  label="ເລີ່ມວັນທີ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  style="min-width: 160px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="dateFrom" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="auto"
          >
            <v-menu
              v-model="dateToMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="dateTo"
                  dense
                  hide-details
                  label="ຫາວັນທີ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  style="min-width: 160px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="dateTo" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="3"
          >
            <v-autocomplete
              v-model="userId"
              :items="staffItems"
              :loading="loadingStaff"
              clearable
              dense
              hide-details
              item-text="text"
              item-value="id"
              label="AR ພາກສະໜາມ"
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-autocomplete
              v-model="districtId"
              :items="districts"
              clearable
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="ເມືອງ"
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-autocomplete
              v-model="villageId"
              :items="villages"
              :disabled="!districtId"
              clearable
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="ບ້ານ"
              outlined
            />
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="auto">
            <v-switch
              v-model="activeOnly"
              class="mt-0"
              dense
              hide-details
              :label="activeOnly ? 'ສະເພາະ AR ທີ່ active' : 'AR ທັງໝົດ'"
            />
          </v-col>
          <v-col cols="auto">
            <v-switch
              v-model="showEmpty"
              class="mt-0"
              dense
              hide-details
              label="ສະແດງຄົນທີ່ບໍ່ມີຍອດ"
            />
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <v-btn
              color="primary"
              depressed
              :loading="loading"
              @click="fetchData"
            >
              <v-icon
                left
                small
              >
                mdi-magnify
              </v-icon>
              ຄົ້ນຫາ
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="success"
              depressed
              :loading="exporting"
              @click="onExport"
            >
              <v-icon
                left
                small
              >
                mdi-microsoft-excel
              </v-icon>
              Export Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col
        v-for="card in cards"
        :key="card.label"
        cols="6"
        md
      >
        <v-card
          class="rounded-lg"
          outlined
        >
          <v-card-text class="pa-4">
            <div class="text-body-2 grey--text mb-1">
              {{ card.label }}
            </div>
            <div :class="['text-h5 font-weight-bold', card.color]">
              {{ card.value }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      class="mt-4"
      outlined
    >
      <v-data-table
        :expanded.sync="expanded"
        :headers="headers"
        :items="rows"
        :items-per-page="-1"
        :loading="loading"
        dense
        hide-default-footer
        item-key="id"
        no-data-text="ບໍ່ມີຂໍ້ມູນໃນຊ່ວງວັນທີນີ້"
        show-expand
        @item-expanded="onExpand"
      >
        <template #[`item.index`]="{ index }">
          {{ index + 1 }}
        </template>
        <template #[`item.cash`]="{ item }">
          {{ formatNumber(item.cash) }}
        </template>
        <template #[`item.ldb_merchant`]="{ item }">
          {{ formatNumber(item.ldb_merchant) }}
        </template>
        <template #[`item.total`]="{ item }">
          <strong>{{ formatNumber(item.total) }}</strong>
        </template>
        <template #expanded-item="{ headers: cols, item }">
          <td
            :colspan="cols.length"
            class="ar-bills-cell"
          >
            <div class="ar-bills">
              <div class="d-flex align-center mb-2">
                <div class="ar-bills-title">
                  <v-icon
                    color="primary"
                    small
                  >
                    mdi-subdirectory-arrow-right
                  </v-icon>
                  ລາຍຊື່ລູກຄ້າ ແລະ ບິນທີ່ {{ item.name }} ເກັບ
                  <span class="grey--text">
                    ({{ formatNumber(item.count_bill) }} ບິນ, {{ formatNumber(item.count_customer) }} ລູກຄ້າ)
                  </span>
                </div>
                <v-spacer />
                <v-text-field
                  v-model="billSearch[item.id]"
                  class="ar-bills-search"
                  clearable
                  dense
                  hide-details
                  outlined
                  placeholder="ຊື່, ເບີໂທ, ເລກບິນ, ບ້ານ"
                  prepend-inner-icon="mdi-magnify"
                />
              </div>
              <v-data-table
                :headers="billHeaders"
                :items="(staffBills[item.id] && staffBills[item.id].items) || []"
                :loading="staffBills[item.id] && staffBills[item.id].loading"
                :search="billSearch[item.id]"
                :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
                :items-per-page="20"
                class="elevation-0 ar-bills-table"
                dense
                no-data-text="ບໍ່ມີບິນ"
                sort-by="paided_at"
              >
                <template #[`item.paided_at`]="{ item: bill }">
                  {{ formatDateTime(bill.paided_at) }}
                </template>
                <template #[`item.customer_name`]="{ item: bill }">
                  {{ bill.customer_name || '-' }}
                </template>
                <template #[`item.village_name`]="{ item: bill }">
                  {{ bill.village_name || '-' }}
                  <div class="grey--text ar-bills-sub">
                    {{ bill.district_name }}
                  </div>
                </template>
                <template #[`item.bill_date`]="{ item: bill }">
                  {{ formatMonth(bill.bill_date) }}
                </template>
                <template #[`item.payment_method`]="{ item: bill }">
                  <v-chip
                    :color="bill.payment_method === 'cash' ? 'green' : 'blue'"
                    dark
                    x-small
                  >
                    {{ bill.payment_method === 'cash' ? 'ເງິນສົດ' : 'LDB merchant' }}
                  </v-chip>
                  <div
                    v-if="bill.reference"
                    class="grey--text ar-bills-sub"
                  >
                    {{ bill.reference }}
                  </div>
                </template>
                <template #[`item.total`]="{ item: bill }">
                  {{ formatNumber(bill.total) }}
                </template>
              </v-data-table>
            </div>
          </td>
        </template>
        <template
          v-if="rows.length"
          #[`body.append`]
        >
          <tr class="font-weight-bold">
            <td />
            <td>ລວມ</td>
            <td class="text-right">
              {{ formatNumber(summary.count_bill) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.count_customer) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.cash) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.ldb_merchant) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.total) }}
            </td>
            <td />
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const emptySummary = () => ({
  cash: 0, ldb_merchant: 0, total: 0, count_bill: 0, count_customer: 0,
});

export default {
  title() {
    return 'Vientiane Waste Co-Dev|AR field collection';
  },
  data() {
    return {
      dateFrom: `${today().slice(0, 8)}01`,
      dateTo: today(),
      dateFromMenu: false,
      dateToMenu: false,
      userId: null,
      districtId: null,
      villageId: null,
      activeOnly: true,
      showEmpty: false,
      staff: [],
      districts: [],
      villages: [],
      loadingStaff: false,
      loading: false,
      exporting: false,
      rows: [],
      summary: emptySummary(),
      expanded: [],
      headers: [
        {
          text: '#', value: 'index', sortable: false, width: 50,
        },
        { text: 'ຊື່', value: 'name' },
        { text: 'ບິນ', value: 'count_bill', align: 'end' },
        { text: 'ລູກຄ້າ', value: 'count_customer', align: 'end' },
        { text: 'ເງິນສົດ', value: 'cash', align: 'end' },
        { text: 'LDB merchant', value: 'ldb_merchant', align: 'end' },
        { text: 'ລວມ', value: 'total', align: 'end' },
        { text: '', value: 'data-table-expand' },
      ],
      // Bills of each expanded AR, loaded on first expand: { [staffId]: { loading, items } }.
      staffBills: {},
      billSearch: {},
      billHeaders: [
        { text: 'ວັນທີຈ່າຍ', value: 'paided_at' },
        { text: 'ເລກບິນ', value: 'display_id' },
        { text: 'ລູກຄ້າ', value: 'customer_name' },
        { text: 'ເບີໂທ', value: 'phone' },
        { text: 'ບ້ານ / ເມືອງ', value: 'village_name' },
        { text: 'ບິນເດືອນ', value: 'bill_date' },
        { text: 'ລາຍການ', value: 'content' },
        { text: 'ວິທີຈ່າຍ', value: 'payment_method' },
        { text: 'ຈຳນວນເງິນ', value: 'total', align: 'end' },
      ],
    };
  },
  computed: {
    cards() {
      return [
        { label: 'ລວມທັງໝົດ (LAK)', value: this.formatNumber(this.summary.total), color: '' },
        { label: 'ເງິນສົດ', value: this.formatNumber(this.summary.cash), color: 'green--text' },
        { label: 'LDB merchant', value: this.formatNumber(this.summary.ldb_merchant), color: 'blue--text' },
        { label: 'ບິນ', value: this.formatNumber(this.summary.count_bill), color: '' },
        { label: 'ລູກຄ້າ', value: this.formatNumber(this.summary.count_customer), color: '' },
      ];
    },
    staffItems() {
      return this.staff.map((user) => ({
        id: user.id,
        text: [user.emp_name ? `${user.emp_name} ${user.emp_surname || ''}` : user.name, user.phone]
          .filter(Boolean).join(' - '),
      }));
    },
  },
  watch: {
    async districtId(id) {
      this.villageId = null;
      this.villages = [];
      if (!id) return;
      try {
        const res = await this.$axios.get(`info/district/${id}/village`);
        this.villages = (res.data && res.data.data) || [];
      } catch (_) {
        this.villages = [];
      }
    },
  },
  created() {
    this.fetchStaff();
    this.fetchDistricts();
    this.fetchData();
  },
  methods: {
    formatNumber(value) {
      return Intl.NumberFormat().format(value || 0);
    },
    formatDateTime(value) {
      if (!value) return '-';
      const [date, time] = String(value).split(' ');
      const [y, m, d] = date.split('-');
      return `${d}/${m}/${y} ${(time || '').slice(0, 5)}`.trim();
    },
    formatMonth(value) {
      if (!value) return '-';
      const [y, m] = String(value).split('-');
      return `${m}/${y}`;
    },
    async onExpand({ item, value }) {
      if (!value || (this.staffBills[item.id] && this.staffBills[item.id].items)) return;
      // Vue 2 only tracks keys that exist, so create the search key before v-model uses it.
      if (!(item.id in this.billSearch)) this.$set(this.billSearch, item.id, '');
      this.$set(this.staffBills, item.id, { loading: true, items: null });
      try {
        const res = await this.$axios.get(`v2/report-ar-field-collection/${item.id}/bills`, { params: this.params() });
        this.$set(this.staffBills, item.id, { loading: false, items: (res.data && res.data.data) || [] });
      } catch (error) {
        this.$set(this.staffBills, item.id, { loading: false, items: null });
        this.notifyError(error);
      }
    },
    params() {
      const params = {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        active_only: String(this.activeOnly),
        show_empty: String(this.showEmpty),
      };
      if (this.userId) params.user_id = this.userId;
      if (this.districtId) params.district_id = this.districtId;
      if (this.villageId) params.village_id = this.villageId;
      return params;
    },
    async fetchStaff() {
      this.loadingStaff = true;
      try {
        const res = await this.$axios.get('user-setting/user', {
          params: { 'roles[]': 'ar_field', per_page: 1000 },
        });
        const data = (res.data && res.data.data) || [];
        this.staff = Array.isArray(data) ? data : data.data || [];
      } catch (_) {
        this.staff = [];
      } finally {
        this.loadingStaff = false;
      }
    },
    async fetchDistricts() {
      try {
        const res = await this.$axios.get('info/district', { params: { province_id: 1 } });
        this.districts = (res.data && res.data.data) || [];
      } catch (_) {
        this.districts = [];
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await this.$axios.get('v2/report-ar-field-collection', { params: this.params() });
        const data = (res.data && res.data.data) || {};
        this.rows = data.data || [];
        this.summary = { ...emptySummary(), ...(data.summary || {}) };
        this.expanded = [];
        this.staffBills = {};
        this.billSearch = {};
      } catch (error) {
        this.rows = [];
        this.summary = emptySummary();
        this.notifyError(error);
      } finally {
        this.loading = false;
      }
    },
    async onExport() {
      this.exporting = true;
      try {
        const res = await this.$axios.get('v2/report-ar-field-collection/export', { params: this.params() });
        const link = res.data && res.data.data && res.data.data.download_link;
        if (link) window.open(link);
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.exporting = false;
      }
    },
    notifyError(error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'ເກີດຂໍ້ຜິດພາດ';
      this.$store.commit('Toast_State', { value: true, color: 'error', msg: message });
    },
  },
};
</script>

<style scoped>
/* The opened AR row stands out; its bills sit indented under it in a tinted panel
   with smaller text, so they do not read as more AR rows. */
::v-deep .v-data-table__expanded__row {
  background: #e3f2fd;
  font-weight: 500;
}

::v-deep .v-data-table__expanded__content {
  box-shadow: none !important;
}

.ar-bills-cell {
  padding: 4px 8px 12px 40px !important;
  background: #fafafa;
}

.ar-bills {
  border-left: 4px solid #1976d2;
  border-radius: 0 4px 4px 0;
  background: #f5f9fd;
  padding: 8px 12px;
}

.ar-bills-title {
  font-size: 12px;
  font-weight: 500;
}

.ar-bills-search {
  max-width: 240px;
  font-size: 12px;
}

.ar-bills-sub {
  font-size: 10px;
  line-height: 1.2;
}

.ar-bills-table {
  background: transparent !important;
}

.ar-bills-table ::v-deep th,
.ar-bills-table ::v-deep td {
  font-size: 11px !important;
  height: 26px !important;
  padding: 0 8px !important;
}

.ar-bills-table ::v-deep .v-data-footer {
  font-size: 11px;
}
</style>
